import { useCallback, useEffect } from 'react';

import { getGiftCouponSettlementSummary } from '@shared/api/couponSettlement/couponSettlement.controller';
import { GiftCouponSettlementSummaryItem } from '@shared/api/couponSettlement/couponSettlement.interface';
import { listGiftRebateSettlements } from '@shared/api/giftRebateSettlement/giftRebateSettlement.controller';
import { GiftRebateSettlement } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import { LowerRole } from '@shared/types';
import { useAsyncFn } from 'react-use';

// ─────────────────────────────────────────────
// useAdminUnifiedSettlementData
//
// 관리자·마리(GiftAgent)의 '상조 답례품 정산 관리' 통합 탭 데이터.
// 배송형은 정산 문서 목록(상조명 표시용 bugoBrand만 populate — 라인 배송 populate는
// 상세 화면 몫이라 여기선 뺀다), 쿠폰형은 경량 summary API를 쓴다.
// 마리는 /gift-agent prefix로 조회해 백엔드가 viaMari 상조만 스코프한다.
// ─────────────────────────────────────────────

export interface AdminUnifiedSettlementData {
  rebateItems: GiftRebateSettlement[] | undefined;
  rebateLoading: boolean;
  couponItems: GiftCouponSettlementSummaryItem[] | undefined;
  couponLoading: boolean;
  /** 두 목록 재조회 (집행·지급확정 후 호출) */
  refresh: () => void;
}

export function useAdminUnifiedSettlementData(
  giftAgentView: boolean,
): AdminUnifiedSettlementData {
  const role = giftAgentView ? LowerRole.GiftAgent : LowerRole.Admin;

  const [{ loading: rebateLoading, value: rebateItems }, fetchRebate] = useAsyncFn(
    () =>
      listGiftRebateSettlements(
        {},
        [{ path: 'bugoBrand', select: 'bugoAgency giftAccountConfig' }],
        { sort: { year: -1, month: -1 } },
        role,
      ),
    [role],
  );

  const [{ loading: couponLoading, value: couponValue }, fetchCoupon] = useAsyncFn(
    () => getGiftCouponSettlementSummary({}, role),
    [role],
  );

  // 참조가 안정적이어야 호출부가 effect 의존성·콜백으로 그대로 쓸 수 있다
  const refresh = useCallback(() => {
    fetchRebate();
    fetchCoupon();
  }, [fetchRebate, fetchCoupon]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    rebateItems,
    rebateLoading,
    couponItems: couponValue?.items,
    couponLoading,
    refresh,
  };
}
